import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/MeteringRecordsService';
import { MeteringRecord } from '../types';

export const useMeteringRecord = (meteringRecordId?: string) => {
    const queryClient = useQueryClient();

    // FETCH
    const meteringRecordQuery = useQuery({
        queryKey: ['metering-record', meteringRecordId],
        queryFn: () => fetch(meteringRecordId!),
        enabled: !!meteringRecordId
    });

    // CREATE MUTATION
    const createMutation = useMutation({
        mutationFn: (values: MeteringRecord) => {
            const { created: _created, updated: _updated, ...rest } = values;
            return add(rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['metering-records'] });
        }
    });

    // UPDATE MUTATION
    const updateMutation = useMutation({
        mutationFn: (values: MeteringRecord) => {
            if (!meteringRecordId) throw new Error('meteringRecordId is required parameter');
            const { created: _created, updated: _updated, ...rest } = values;
            return update(meteringRecordId, rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['metering-records'] });
            queryClient.invalidateQueries({ queryKey: ['metering-record', meteringRecordId] });
        }
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!meteringRecordId) throw new Error('meteringRecordId is required parameter');
            return deleteRecord(meteringRecordId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['metering-records'] });
        }
    });

    return {
        record: meteringRecordQuery.data,
        loading: meteringRecordQuery.isLoading,
        fetchRecord: meteringRecordQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};